/**
 * API Service - Handles all HTTP requests to backend
 */

import { stripMITechniqueLabels } from '../utils/miTechniques';
import { isDevHost } from '../utils/devHost';

const API_URL = process.env.REACT_APP_API_URL || '';

// Module-level cache for auth token (avoids localStorage reads on every API call)
let cachedToken = null;

// Module-level cache for in-flight requests (prevents duplicate API calls)
const inflightRequests = new Map();

/**
 * Get auth token from localStorage (with in-memory cache)
 */
function getToken() {
  if (cachedToken !== null) {
    return cachedToken;
  }
  cachedToken = localStorage.getItem('authToken');
  return cachedToken;
}

/**
 * Set auth token in localStorage and cache
 */
function setToken(token) {
  cachedToken = token; // Update cache immediately
  localStorage.setItem('authToken', token);
}

/**
 * Remove auth token from localStorage and clear cache
 */
function removeToken() {
  cachedToken = null; // Clear cache
  localStorage.clear(); // Clear ALL localStorage to prevent data leaking between accounts
}

/**
 * Clean MI technique labels from text for display
 * Removes both &label& and {{label}} formats
 */
function cleanMILabels(text) {
  if (!text) return text;
  return stripMITechniqueLabels(text)          // &labels& and **&labels&**
    .replace(/\*\*\{\{[^}]*\}\}\*\*/g, '')     // **{{bold labels}}**
    .replace(/\{\{[^}]*\}\}/g, '')             // {{labels}}
    .trim();
}

/**
 * Make authenticated API request with deduplication
 */
async function fetchAPI(endpoint, options = {}) {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Create request key for deduplication (method + endpoint + body)
  const method = options.method || 'GET';
  const bodyKey = options.body ? JSON.stringify(options.body) : '';
  const requestKey = `${method}:${endpoint}:${bodyKey}`;

  // Check if identical request is already in flight
  if (inflightRequests.has(requestKey)) {
    // console.log('[fetchAPI] Deduplicating request:', requestKey);
    return inflightRequests.get(requestKey);
  }

  // Create request promise
  const requestPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('[fetchAPI] Error response:', error);

        // Only reload on 401 if we have a token (expired/invalid token)
        // Don't reload on login failures (wrong credentials)
        if (response.status === 401 && token && endpoint !== '/api/auth/login') {
          // console.log('[fetchAPI] 401 with valid token - removing and reloading');
          removeToken();
          window.location.reload();
        }

        // Carry the status + full body on the thrown error so callers can react
        // to flags beyond the message (e.g. requiresPasswordSetup from local auth).
        const err = new Error(error.error || 'Something happened! Try again later.');
        err.status = response.status;
        err.payload = error;
        throw err;
      }

      const data = await response.json();

      // Expose the server's clock to callers that need to age server-written
      // timestamps (message freshness). The CLIENT clock cannot be trusted
      // for this — observed hours of skew against the server in the field.
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const serverDate = response.headers.get('date');
        if (serverDate) data._serverDate = serverDate;
      }

      // Check for awarded beads and emit global event for immediate display
      if (data?.awardedBeads?.length > 0) {
        console.log('[fetchAPI] 🎁 Beads awarded in response:', data.awardedBeads);
        // Emit event for each bead (App.jsx listens for this)
        data.awardedBeads.forEach(bead => {
          window.dispatchEvent(new CustomEvent('beadAwarded', { detail: bead }));
        });
      }

      return data;
    } catch (error) {
      console.error('[fetchAPI] Request failed:', {
        url: endpoint,
        error: error.message,
        type: error.constructor.name
      });
      // Handle network errors (server down, no connection, etc.)
      if (error instanceof TypeError || error.message?.includes('fetch')) {
        throw new Error('Server is unavailable. Please try again later.');
      }
      // Re-throw other errors (already Error objects with message)
      throw error;
    } finally {
      // Clean up inflight request after completion (success or error)
      inflightRequests.delete(requestKey);
    }
  })();

  // Store in-flight request
  inflightRequests.set(requestKey, requestPromise);

  return requestPromise;
}

/**
 * Auth API
 */
export const authAPI = {
  async login(email, password, orgSlug) {
    // console.log('[API] ===== SENDING LOGIN REQUEST =====');
    // console.log('[API] Email:', email);
    // console.log('[API] Endpoint:', `${API_URL}/api/auth/login`);

    try {
      const data = await fetchAPI('/api/auth/login', {
        method: 'POST',
        // orgSlug: logging in via an organization's register link attaches
        // the membership to the existing account (local auth).
        body: JSON.stringify({ email, password, orgSlug: orgSlug || undefined })
      });
      // console.log('[API] ✅ Login response received');
      // console.log('[API] Response keys:', Object.keys(data));
      // console.log('[API] Response:', data);

      // Development mode: direct token response
      if (data.token) {
        // console.log('[API] Dev mode detected - storing token');
        // console.log('[API] Token preview:', data.token.substring(0, 20) + '...');
        setToken(data.token);
        return data;
      }

      // Production mode: OAuth redirect
      if (data.ssoUrl) {
        // console.log('[API] 🔄 Production mode - OAuth redirect');
        // console.log('[API] Redirect URL:', data.ssoUrl);
        // console.log('[API] Redirecting to Thinkific...');
        window.location.href = data.ssoUrl;
        return data;
      }

      console.warn('[API] ⚠️ Unexpected response - no token or ssoUrl');
      return data;
    } catch (error) {
      console.error('[API] ❌ Login request failed:', error.message);
      throw error;
    }
  },

  /**
   * Local auth (REACT_APP_AUTH_PROVIDER=local): create an account.
   * Server returns the same { token, user } shape as login.
   */
  async register(email, password, firstName, lastName, orgSlug) {
    const data = await fetchAPI('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName, orgSlug: orgSlug || undefined })
    });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Local auth: a logged-in user opened an organization's register link —
   * attach the membership to their existing account (no re-registration).
   */
  async joinOrg(orgSlug) {
    return fetchAPI('/api/auth/join-org', {
      method: 'POST',
      body: JSON.stringify({ orgSlug })
    });
  },

  /** Local auth: request a password reset email. Always resolves ok. */
  async forgotPassword(email) {
    return fetchAPI('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  /**
   * Local auth: consume a reset/setup link token and set the new password.
   * Server logs the user straight in — returns { token, user }.
   */
  async resetPassword(token, password) {
    const data = await fetchAPI('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  async getCurrentUser() {
    return fetchAPI('/api/auth/me');
  },

  async updateProfile(profileData) {
    // console.log('[API] Updating user profile:', profileData);
    return fetchAPI('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  async saveProfileAnswer(field, value, savesToProfile = true) {
    return fetchAPI('/api/auth/profile-answer', {
      method: 'POST',
      body: JSON.stringify({ field, value, savesToProfile })
    });
  },

  async logout() {
    let data = null;
    try {
      data = await fetchAPI('/api/auth/logout', {
        method: 'POST'
      });
    } catch (error) {
      // Even if logout fails on the server, we still clear local state below.
      console.error('[API] Logout request failed (continuing anyway):', error.message);
    }

    // Clear local token / cached state.
    removeToken();

    // Belt-and-braces: if the next login happens in this same tab, force Thinkific to
    // re-prompt for credentials (prompt=login). sessionStorage survives removeToken()
    // (which only clears localStorage) and the full-page redirect below.
    try { sessionStorage.setItem('sophia_force_reauth', '1'); } catch (e) { /* ignore */ }

    // The Thinkific session cookie can't be cleared from our domain (cross-site), so a
    // top-level navigation to Thinkific's sign-out URL is the only reliable way to end
    // it — otherwise the next OAuth round-trip silently signs the same user back in.
    // Thinkific has no post-sign-out redirect, so the user lands on the school homepage.
    // In dev (DEV_PASSWORD login, no Thinkific session) just return to the app.
    // With local auth (REACT_APP_AUTH_PROVIDER=local) there is no Thinkific session
    // at all — clearing our own token above IS the logout, so stay on the app.
    const isLocalAuth = process.env.REACT_APP_AUTH_PROVIDER === 'local';
    const thinkificLogoutUrl = !isLocalAuth && !isDevHost() && data && data.thinkificLogoutUrl;
    window.location.href = thinkificLogoutUrl || (data && data.appUrl) || '/';
    return data;
  }
};

/**
 * Chat API
 */
export const chatAPI = {
  async getHistory() {
    // console.log('[API] Getting chat history');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return fetchAPI(`/api/chat/history${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`);
  },

  async getAllHistory() {
    // console.log('[API] Getting all chat history');
    return fetchAPI('/api/chat/history/all');
  },

  async sendMessage(message, action = null, options = {}) {
    // console.log('[API] Sending chat message:', message, 'action:', action);
    const payload = { message };
    if (action !== null && action !== undefined) {
      payload.action = action;
    }
    // isHidden: message provides context to LLM but shouldn't appear in chat history
    if (options.isHidden) {
      payload.isHidden = true;
    }
    // Suggestion-chip tap metadata (see useChat.sendSuggestion)
    if (options.suggestionId !== undefined && options.suggestionId !== null) {
      payload.suggestionId = options.suggestionId;
    }
    if (options.undoTopic) {
      payload.undoTopic = options.undoTopic;
    }
    const data = await fetchAPI('/api/chat/message', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    // console.log('[API] Chat message response:', data);
    return data;
  },

  async streamMessage(message, action = null, onChunk, options = {}) {
    // console.log('[API] Streaming chat message:', message, 'action:', action);
    const token = getToken();

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    // Same payload contract as sendMessage — the stream endpoint reached
    // feature parity (isHidden / undoTopic / suggestions) in the server's
    // stream-parity branch; keep the two in lockstep.
    const payload = { message };
    if (action !== null && action !== undefined) {
      payload.action = action;
    }
    if (options.isHidden) {
      payload.isHidden = true;
    }
    if (options.suggestionId !== undefined && options.suggestionId !== null) {
      payload.suggestionId = options.suggestionId;
    }
    if (options.undoTopic) {
      payload.undoTopic = options.undoTopic;
    }

    const response = await fetch(`${API_URL}/api/chat/message/stream`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to stream message');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let conversationId = null;
    let streamingMessage = { content: '', timestamp: new Date().toISOString() };
    let planMessages = []; // Store plan messages that arrive after main response
    let suggestions = null; // Suggestion chips (get_next_topic ran this turn)
    let topicMarkedThisTurn = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'start') {
              conversationId = data.conversationId;
            } else if (data.type === 'chunk') {
              streamingMessage.content += data.content;
              // Live-display cleaning: complete {{behavioral}} tags, plus any
              // PARTIAL tag still streaming in at the tail ("…doable? {{" was
              // visibly leaking before the closing braces arrived).
              const liveText = cleanMILabels(streamingMessage.content)
                .replace(/\{\{[^}]*\}\}/g, '')
                .replace(/\{\{[^}]*$/, '')
                .replace(/<(?:TodoWidget|TodoPlan|Article|Video|Disclaimer|Tip|Meeting|Resource|Quiz|Notification):[^>]*$/, '');
              onChunk(liveText);
            } else if (data.type === 'done') {
              // Store the main response but don't return yet
              // (might have plan_message or tool_response events coming)
              streamingMessage.userMessage = data.userMessage;
              streamingMessage.assistantMessage = data.assistantMessage;
            } else if (data.type === 'plan_message') {
              // Capture plan messages sent after tool execution
              // console.log('[API] Received plan message:', data.message);
              planMessages.push(data.message);
            } else if (data.type === 'tool_response') {
              // Capture tool response (Claude's final response after seeing tool results)
              console.log('[API] Received tool response:', data.message);
              console.log('[API] Tool response content length:', data.message.content?.length);
              console.log('[API] Tool response preview:', data.message.content?.substring(0, 200));

              // Add the tool response as a separate message
              if (!streamingMessage.toolResponses) {
                streamingMessage.toolResponses = [];
              }
              streamingMessage.toolResponses.push(data.message);

              // CRITICAL: Update streamingMessage.content to include the tool response
              // This ensures the content is persisted, not just displayed temporarily
              streamingMessage.content = streamingMessage.content + '\n' + data.message.content;

              // Also update the display in real-time (clean MI labels for display)
              onChunk(cleanMILabels(streamingMessage.content));
            } else if (data.type === 'widget_message') {
              // Capture widget messages sent after tool execution
              // console.log('[API] Received widget message:', data.message);
              if (!streamingMessage.widgetMessages) {
                streamingMessage.widgetMessages = [];
              }
              streamingMessage.widgetMessages.push(data.message);
            } else if (data.type === 'celebration_message') {
              // Inline Celebrate! bubble (a delivered queued fact, saved
              // server-side as a <Celebration:{...}> message). Surface it
              // IMMEDIATELY — it arrives while Sophia is still thinking —
              // and also return it so useChat's final assembly can dedupe.
              if (!streamingMessage.celebrationMessages) {
                streamingMessage.celebrationMessages = [];
              }
              streamingMessage.celebrationMessages.push(data.message);
              window.dispatchEvent(new CustomEvent('celebrationMessage', { detail: data.message }));
            } else if (data.type === 'suggestions') {
              // Chips arrive as their own event just before the stream closes
              suggestions = data.suggestions || [];
              topicMarkedThisTurn = data.topicMarkedThisTurn || null;
            } else if (data.type === 'bead_awarded') {
              // Bead awarded from behavioral threshold during chat
              console.log('[API] Bead awarded during stream:', data.bead);
              window.dispatchEvent(new CustomEvent('beadAwarded', { detail: data.bead }));
            } else if (data.type === 'todos_updated') {
              // Todos were modified (added/edited/removed)
              console.log('[API] Todos updated - setting flag for refetch');
              streamingMessage.todosUpdated = true;
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e) {
            console.error('[API] Error parsing SSE data:', e);
            // Skip invalid JSON
          }
        }
      }
    }

    // Return all messages including plan messages, tool responses, and widget messages
    return {
      conversationId,
      userMessage: streamingMessage.userMessage,
      assistantMessage: streamingMessage.assistantMessage,
      planMessages: planMessages,
      toolResponses: streamingMessage.toolResponses || [],
      widgetMessages: streamingMessage.widgetMessages || [],
      celebrationMessages: streamingMessage.celebrationMessages || [],
      todosUpdated: streamingMessage.todosUpdated || false,
      ...(suggestions !== null ? { suggestions } : {}),
      ...(topicMarkedThisTurn ? { topicMarkedThisTurn } : {})
    };
  },

  async clearConversation() {
    // console.log('[API] Clearing conversation');
    return fetchAPI('/api/chat/clear', {
      method: 'POST'
    });
  }
};

/**
 * Todos API
 */
export const todosAPI = {
  async getTodos(conversationId) {
    // console.log('[API] Getting todos for conversation:', conversationId);
    const url = conversationId ? `/api/todos?conversationId=${conversationId}` : '/api/todos';
    return fetchAPI(url);
  },

  async toggleTodo(todoId) {
    // console.log('[API] Toggling todo:', todoId);
    return fetchAPI(`/api/todos/${todoId}/toggle`, {
      method: 'PUT'
    });
  },

  async getProgressSummary(conversationId) {
    // console.log('[API] Getting progress summary for conversation:', conversationId);
    return fetchAPI(`/api/todos/progress?conversationId=${conversationId}`);
  },

  async updateTodo(todoId, todoText) {
    // console.log('[API] Updating todo:', todoId, 'New text:', todoText);
    return fetchAPI(`/api/todos/${todoId}/update`, {
      method: 'PUT',
      body: JSON.stringify({ todo_text: todoText })
    });
  },

  async deleteTodo(todoId) {
    // console.log('[API] Deleting todo:', todoId);
    return fetchAPI(`/api/todos/${todoId}`, {
      method: 'DELETE'
    });
  },

  async updateStage(conversationId, tierNumber, tierTitle) {
    // console.log('[API] Updating stage:', conversationId, tierNumber, 'New title:', tierTitle);
    return fetchAPI(`/api/todos/stage/${conversationId}/${tierNumber}`, {
      method: 'PUT',
      body: JSON.stringify({ tier_title: tierTitle })
    });
  },

  async deleteStage(conversationId, tierNumber) {
    // console.log('[API] Deleting stage:', conversationId, tierNumber);
    return fetchAPI(`/api/todos/stage/${conversationId}/${tierNumber}`, {
      method: 'DELETE'
    });
  },

  async approvePlan(conversationId, plan, objective, wasCompleted) {
    // console.log('[API] Approving plan for conversation:', conversationId);
    return fetchAPI('/api/todos/approve', {
      method: 'POST',
      body: JSON.stringify({
        conversationId,
        plan,
        objective,
        wasCompleted
      })
    });
  },

  async proposePlan(missionText, templateId = null, conversationContext = null, existingPlan = null) {
    console.log('[API] Proposing plan for mission:', missionText);
    console.log('[API] Has existing plan:', !!existingPlan);
    return fetchAPI('/api/todos/propose', {
      method: 'POST',
      body: JSON.stringify({
        missionText,
        templateId,
        conversationContext,
        existingPlan
      })
    });
  }
};

/**
 * Content API (Articles, News, Videos)
 */
export const contentAPI = {
  async getArticles(search = '') {
    const endpoint = search ? `/api/content/articles?search=${encodeURIComponent(search)}` : '/api/content/articles';
    return fetchAPI(endpoint);
  },

  async getArticle(slug) {
    return fetchAPI(`/api/content/articles/${slug}`);
  },

  async getNews(search = '') {
    const endpoint = search ? `/api/content/news?search=${encodeURIComponent(search)}` : '/api/content/news';
    return fetchAPI(endpoint);
  },

  async getNewsItem(id) {
    return fetchAPI(`/api/content/news/${id}`);
  },

  async getVideos(search = '') {
    const endpoint = search ? `/api/content/videos?search=${encodeURIComponent(search)}` : '/api/content/videos';
    return fetchAPI(endpoint);
  },

  async getVideo(id) {
    return fetchAPI(`/api/content/videos/${id}`);
  },

  // Resolve a guaranteed-fresh playback URL (re-signs stale Thinkific URLs).
  async getVideoPlayUrl(id) {
    return fetchAPI(`/api/content/videos/${id}/play-url`);
  },

  async getLearningResources(search = '') {
    const endpoint = search ? `/api/content/learning-resources?search=${encodeURIComponent(search)}` : '/api/content/learning-resources';
    return fetchAPI(endpoint);
  },

  // Content Sophia has shown the current user in chat (for "Shared with you"
  // badge + ordering). Match to a resource on (contentType, sourceId === id).
  async getSharedContent() {
    return fetchAPI('/api/content/shared');
  },

  async getStats() {
    return fetchAPI('/api/content/stats');
  }
};

/**
 * Loading Screen API
 */
export const loadingScreenAPI = {
  async getPersonalizedContent() {
    return fetchAPI('/api/loading-screen');
  },

  /**
   * Get session-based loading screen content
   * @param {Object} params - Session parameters
   * @param {boolean} params.isFirstScreen - Is this the first screen of the session?
   * @param {number} params.lastCelebrationCount - Screens shown since last celebration
   * @returns {Promise<Object>} Content data with type, message, and optional videoFile
   */
  async getContent({ isFirstScreen, lastCelebrationCount }) {
    const params = new URLSearchParams({
      isFirstScreen: String(isFirstScreen),
      lastCelebrationCount: String(lastCelebrationCount)
    });

    return fetchAPI(`/api/loading-screen/get-content?${params}`);
  },

  /**
   * Mark a queued fact as displayed
   * @param {number} factId - The fact ID to mark as displayed
   * @returns {Promise<Object>} Success response
   */
  async markDisplayed(factId) {
    return fetchAPI(`/api/loading-screen/mark-displayed/${factId}`, {
      method: 'POST'
    });
  },

  /**
   * Get all active motivational messages for the chat "thinking" indicator
   * @returns {Promise<{ messages: string[] }>} Array of motivational message strings
   */
  async getMotivationalMessages() {
    return fetchAPI('/api/loading-screen/motivational-messages');
  }
};

/**
 * Bookmarks API
 */
export const bookmarksAPI = {
  async getBookmarks(contentType = null) {
    const endpoint = contentType ? `/api/bookmarks?contentType=${contentType}` : '/api/bookmarks';
    return fetchAPI(endpoint);
  },

  async isBookmarked(contentType, contentId) {
    return fetchAPI(`/api/bookmarks/check/${contentType}/${contentId}`);
  },

  async toggleBookmark(contentType, contentId) {
    return fetchAPI('/api/bookmarks/toggle', {
      method: 'POST',
      body: JSON.stringify({ contentType, contentId })
    });
  },

  async getCounts() {
    return fetchAPI('/api/bookmarks/counts');
  }
};

/**
 * Push Notifications API
 */
export const pushAPI = {
  async getVapidPublicKey() {
    return fetchAPI('/api/push/vapid-public-key');
  },

  async subscribe(subscription) {
    return fetchAPI('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription })
    });
  },

  async unsubscribe(endpoint) {
    return fetchAPI('/api/push/unsubscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint })
    });
  },

  async getSubscriptions() {
    return fetchAPI('/api/push/subscriptions');
  }
};

/**
 * Progress API (Dashboard Stats)
 */
export const progressAPI = {
  async getStats() {
    return fetchAPI('/api/progress/stats');
  },

  async getBenchmarks() {
    return fetchAPI('/api/progress/benchmarks');
  },

  async getJourneyInsights() {
    return fetchAPI('/api/progress/journey-insights');
  }
};

/**
 * Coaches API - For user browsing and booking coaches
 */
export const coachesAPI = {
  async getAllCoaches() {
    return fetchAPI('/api/coaches');
  },

  async searchCoaches(query, limit = 3) {
    return fetchAPI('/api/coaches/search', {
      method: 'POST',
      body: JSON.stringify({ query, limit })
    });
  },

  async getCoach(coachId) {
    return fetchAPI(`/api/coaches/${coachId}`);
  },

  async getCoachSlots(coachId, from, to) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const queryString = params.toString();
    return fetchAPI(`/api/coaches/${coachId}/slots${queryString ? `?${queryString}` : ''}`);
  }
};

/**
 * Sessions API - For user session management
 */
export const sessionsAPI = {
  async bookSession(coachId, scheduledStart) {
    return fetchAPI('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({ coachId, scheduledStart })
    });
  },

  async getUpcomingSessions() {
    return fetchAPI('/api/sessions/upcoming');
  },

  async getNextSession() {
    return fetchAPI('/api/sessions/next');
  },

  // Join status for one session — also triggers a server-side retry of the
  // Zoom meeting creation when the session has no link yet.
  async getJoinStatus(sessionId) {
    return fetchAPI(`/api/sessions/${sessionId}/join`);
  },

  async getSessionHistory(limit = 20, offset = 0) {
    return fetchAPI(`/api/sessions/history?limit=${limit}&offset=${offset}`);
  },

  async getSession(sessionId) {
    return fetchAPI(`/api/sessions/${sessionId}`);
  },

  async cancelSession(sessionId, reason = null) {
    return fetchAPI(`/api/sessions/${sessionId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  async rescheduleSession(sessionId, newStartTime) {
    return fetchAPI(`/api/sessions/${sessionId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ newStartTime })
    });
  }
};

/**
 * Enterprise Dashboard API - For organization admins
 */
export const enterpriseDashboardAPI = {
  async getCompanies() {
    return fetchAPI('/api/enterprise/companies');
  },

  async getDashboard(organizationId) {
    return fetchAPI(`/api/enterprise/dashboard/${organizationId}`);
  },

  async getTagTrends(organizationId) {
    return fetchAPI(`/api/enterprise/tag-trends/${organizationId}`);
  },

  async syncCompanies() {
    return fetchAPI('/api/enterprise/sync', {
      method: 'POST'
    });
  }
};

/**
 * Coach Dashboard API - For coach-specific operations
 */
export const coachDashboardAPI = {
  // Profile
  async getMyProfile() {
    return fetchAPI('/api/coaches/me/profile');
  },

  // Availability
  async getMyAvailability() {
    return fetchAPI('/api/coaches/me/availability');
  },

  async addAvailability(dayOfWeek, startTime, endTime, timezone) {
    return fetchAPI('/api/coaches/me/availability', {
      method: 'POST',
      body: JSON.stringify({ dayOfWeek, startTime, endTime, timezone })
    });
  },

  async updateAvailability(availabilityId, updates) {
    return fetchAPI(`/api/coaches/me/availability/${availabilityId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  async deleteAvailability(availabilityId) {
    return fetchAPI(`/api/coaches/me/availability/${availabilityId}`, {
      method: 'DELETE'
    });
  },

  // Blocked slots
  async getBlockedSlots(from, to) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const queryString = params.toString();
    return fetchAPI(`/api/coaches/me/blocked-slots${queryString ? `?${queryString}` : ''}`);
  },

  async addBlockedSlot(blockedStart, blockedEnd, reason = null) {
    return fetchAPI('/api/coaches/me/blocked-slots', {
      method: 'POST',
      body: JSON.stringify({ blockedStart, blockedEnd, reason })
    });
  },

  async deleteBlockedSlot(blockedSlotId) {
    return fetchAPI(`/api/coaches/me/blocked-slots/${blockedSlotId}`, {
      method: 'DELETE'
    });
  },

  // Users
  async getMyUsers() {
    return fetchAPI('/api/coaches/me/users');
  },

  async getUserHistory(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/history`);
  },

  async getUserStats(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/stats`);
  },

  async getUserSummaries(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/summaries`);
  },

  async getUserNotes(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes`);
  },

  async createUserNote(userId, noteText) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ noteText })
    });
  },

  async updateUserNote(userId, noteId, noteText) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ noteText })
    });
  },

  async deleteUserNote(userId, noteId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes/${noteId}`, {
      method: 'DELETE'
    });
  },

  async getUserObservations(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/observations`);
  },

  async getUserTodos(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos`);
  },

  async getUserMessages(userId, options = {}) {
    const { limit = 20, page = 1, allConversations = false } = options;
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', page);
    if (allConversations) {
      params.append('allConversations', 'true');
    }
    return fetchAPI(`/api/coaches/me/users/${userId}/messages?${params.toString()}`);
  },

  async createUserTodo(userId, todoData) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos`, {
      method: 'POST',
      body: JSON.stringify(todoData)
    });
  },

  async updateUserTodo(userId, todoId, updates) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos/${todoId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  async deleteUserTodo(userId, todoId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos/${todoId}`, {
      method: 'DELETE'
    });
  },

  // Stats
  async getMyStats() {
    return fetchAPI('/api/coaches/me/stats');
  },

  // Sessions (coach view)
  async getUpcomingSessions(limit = 20) {
    return fetchAPI(`/api/sessions/coach/upcoming?limit=${limit}`);
  },

  async getTodaySessions() {
    return fetchAPI('/api/sessions/coach/today');
  },

  async getPastSessions(limit = 20) {
    return fetchAPI(`/api/sessions/coach/past?limit=${limit}`);
  },

  async getCoachJoinStatus(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/join`);
  },

  async cancelSession(sessionId, reason = null) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  async rescheduleSession(sessionId, newStartTime) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ newStartTime })
    });
  },

  async completeSession(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/complete`, {
      method: 'POST'
    });
  },

  async markNoShow(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/no-show`, {
      method: 'POST'
    });
  },

  // Notes
  async getSessionNotes(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/notes`);
  },

  async addSessionNote(sessionId, noteText) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ noteText })
    });
  },

  async updateSessionNote(noteId, noteText) {
    return fetchAPI(`/api/sessions/coach/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ noteText })
    });
  },

  // Admin functions (full permission only)
  async getAllCoaches() {
    return fetchAPI('/api/coaches/admin/all');
  },

  async updateCoachStatus(coachId, isActive) {
    return fetchAPI(`/api/coaches/admin/${coachId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive })
    });
  },

  async getCoachAvailability(coachId) {
    return fetchAPI(`/api/coaches/admin/${coachId}/availability`);
  },

  async getSystemStats() {
    return fetchAPI('/api/coaches/admin/stats');
  },

  async regenerateEmbeddings() {
    return fetchAPI('/api/coaches/admin/embeddings/regenerate', {
      method: 'POST'
    });
  }
};

// Quiz API
export const quizAPI = {
  // Get all available quizzes
  async getAvailableQuizzes() {
    return fetchAPI('/api/quiz/available');
  },

  // Get quiz by slug
  async getQuiz(slug) {
    return fetchAPI(`/api/quiz/${slug}`);
  },

  // Submit quiz answers
  async submitQuiz(slug, answers, reflections = null) {
    return fetchAPI(`/api/quiz/${slug}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answers, reflections })
    });
  },

  // Get completed quizzes
  async getCompletedQuizzes() {
    return fetchAPI('/api/quiz/completed');
  },

  // Get quiz history (all attempts)
  async getQuizHistory(slug) {
    return fetchAPI(`/api/quiz/${slug}/history`);
  }
};

/**
 * Beads API (Achievement Bracelet System)
 */
export const beadsAPI = {
  // Get all beads for collection view (with user's earned status)
  async getAllBeads() {
    return fetchAPI('/api/beads/all');
  },

  // Get user's earned beads for bracelet display
  async getEarnedBeads() {
    return fetchAPI('/api/beads/earned');
  },

  // Get count of unviewed beads (for notification badges)
  async getUnviewedCount() {
    return fetchAPI('/api/beads/unviewed-count');
  },

  // Get unviewed beads with full data (for showing unlock modals)
  async getUnviewedBeads() {
    return fetchAPI('/api/beads/unviewed');
  },

  // Get single bead by slug
  async getBead(slug) {
    return fetchAPI(`/api/beads/${slug}`);
  },

  // Get curated stories for a bead
  async getBeadStories(beadId) {
    return fetchAPI(`/api/beads/${beadId}/stories`);
  },

  // Mark a bead as viewed
  async markBeadViewed(userBeadId) {
    return fetchAPI(`/api/beads/${userBeadId}/viewed`, {
      method: 'PUT'
    });
  },

  // Submit user's "how does this feel" response after bead unlock
  async submitStoryResponse(beadId, storyText, messageId = null) {
    return fetchAPI('/api/beads/story-response', {
      method: 'POST',
      body: JSON.stringify({ beadId, storyText, messageId })
    });
  },

  // Get ghost bracelets (anonymized bracelets from similar users)
  async getGhostBracelets() {
    return fetchAPI('/api/beads/ghost-bracelets');
  },

  // Get aggregate stats for social proof
  async getAggregateStats() {
    return fetchAPI('/api/beads/stats/aggregate');
  }
};

/**
 * Onboarding API (reference data for the onboarding flow)
 */
export const onboardingAPI = {
  // Goal cards for the "What matters most to you right now?" step, from the
  // Strapi objective-templates flagged showInOnboarding.
  // Returns { goals: [{ value, label, image, templateId }] }.
  // Callers MUST fall back to the hardcoded GOAL_OPTIONS on error or an empty
  // list — an empty goal picker is a dead end.
  async getGoalOptions() {
    return fetchAPI('/api/onboarding/goals');
  }
};

/**
 * Cycles API (User cycle enrollments + day pointer)
 * Cycles are invisible to users — they guide Claude's conversation topics —
 * but the day pointer is also useful for client-side gating (e.g. only show
 * the onboarding tour on day 1).
 */
export const cyclesAPI = {
  // Get user's active cycles with current day info
  // Response shape: { success, cycles: [{ cycle: { slug, ... }, dayNumber, daysRemaining, ... }] }
  async getActive() {
    return fetchAPI('/api/cycles/active');
  }
};

/**
 * WhatsApp API — link/unlink the user's number to the Sophia WhatsApp bot for
 * notification delivery. The /link call sends a verification message; the user
 * must reply on WhatsApp to flip whatsapp_opt_in=true (the inbound handler does
 * that on the server side).
 */
export const whatsappAPI = {
  // body: { phoneNumber: "+1 809-555-1234" or "18095551234" }
  // response: { pending: true, delivered: bool, verificationSentTo?, reason?, message? }
  async link(phoneNumber) {
    return fetchAPI('/api/whatsapp/link', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber })
    });
  },

  async unlink() {
    return fetchAPI('/api/whatsapp/unlink', {
      method: 'POST'
    });
  },

  // Admin endpoints — only succeed if current user's email is in
  // WHATSAPP_ADMIN_EMAILS on the server. Non-admins get a 403, which the UI
  // uses as the "hide this panel entirely" signal.
  async adminStatus() {
    return fetchAPI('/api/whatsapp/admin/status');
  },

  async adminLogout() {
    return fetchAPI('/api/whatsapp/admin/logout', {
      method: 'POST'
    });
  },

  // Fires a real scheduled_notifications row + send via the existing pipeline.
  // Defaults to the caller — pass { userId } in body to target someone else.
  async adminTestNotification(body = {}) {
    return fetchAPI('/api/whatsapp/admin/test-notification', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
};

/**
 * Meet Sophia API — authenticated endpoints only. The public session
 * endpoints (/check, /start, /message, /finalize, /state) are deliberately
 * NOT here: the pre-auth page calls them via raw fetch (see MeetSophia.jsx)
 * to stay clear of fetchAPI's 401/reload machinery.
 */
export const meetSophiaAPI = {
  // Post-registration reconciliation: if this user's email completed the
  // pre-signup session, the server rehydrates the EXACT previewed plan into
  // a real conversation. Response: { adopted: bool, conversationId?, objective? }
  async adopt() {
    return fetchAPI('/api/meet-sophia/adopt', {
      method: 'POST'
    });
  }
};

/**
 * Money Dates API (Sophia Together) — peer money-experience meetings.
 */
export const moneyDatesAPI = {
  // { optedIn, activeDate: { id, status, partnerFirstName, candidateSlots,
  //   mySlots, scheduledStart, zoomJoinUrl, agenda } | null, history }
  async getState() {
    return fetchAPI('/api/money-dates/me');
  },

  async setOptIn(optIn) {
    return fetchAPI('/api/money-dates/opt-in', {
      method: 'POST',
      body: JSON.stringify({ optIn })
    });
  },

  // slots: array of ISO strings the user can do; books on overlap
  async submitAvailability(moneyDateId, slots) {
    return fetchAPI(`/api/money-dates/${moneyDateId}/availability`, {
      method: 'POST',
      body: JSON.stringify({ slots })
    });
  },

  async decline(moneyDateId) {
    return fetchAPI(`/api/money-dates/${moneyDateId}/decline`, {
      method: 'POST'
    });
  },

  // Answer the "still on?" reconfirm
  async confirm(moneyDateId) {
    return fetchAPI(`/api/money-dates/${moneyDateId}/confirm`, {
      method: 'POST'
    });
  }
};

/**
 * Circles API (Sophia Together) — private groups with a shared plan + chat.
 */
export const circlesAPI = {
  async getMyCircles() {
    return fetchAPI('/api/circles/me');
  },

  async createCircle(name) {
    return fetchAPI('/api/circles', {
      method: 'POST',
      body: JSON.stringify({ name })
    });
  },

  async previewInvite(token) {
    return fetchAPI(`/api/circles/invite/${encodeURIComponent(token)}/preview`);
  },

  async acceptInvite(token) {
    return fetchAPI(`/api/circles/invite/${encodeURIComponent(token)}/accept`, {
      method: 'POST'
    });
  },

  async getCircle(circleId) {
    return fetchAPI(`/api/circles/${circleId}`);
  },

  async regenerateInvite(circleId) {
    return fetchAPI(`/api/circles/${circleId}/invite/regenerate`, { method: 'POST' });
  },

  async leaveCircle(circleId, options = {}) {
    return fetchAPI(`/api/circles/${circleId}/leave`, {
      method: 'POST',
      body: JSON.stringify(options)
    });
  },

  async transferOwner(circleId, newOwnerUserId) {
    return fetchAPI(`/api/circles/${circleId}/transfer-owner`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerUserId })
    });
  },

  async closeCircle(circleId) {
    return fetchAPI(`/api/circles/${circleId}/close`, { method: 'POST' });
  },

  async removeMember(circleId, userId) {
    return fetchAPI(`/api/circles/${circleId}/remove-member`, {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
  },

  async lookupEmail(circleId, email) {
    return fetchAPI(`/api/circles/${circleId}/lookup-email?email=${encodeURIComponent(email)}`);
  },

  async inviteByEmail(circleId, email, addToFamily = false) {
    return fetchAPI(`/api/circles/${circleId}/invite-email`, {
      method: 'POST',
      body: JSON.stringify({ email, addToFamily })
    });
  },

  async proposePlan(circleId, missionText) {
    return fetchAPI(`/api/circles/${circleId}/plan/propose`, {
      method: 'POST',
      body: JSON.stringify({ missionText })
    });
  },

  async approvePlan(circleId, missionText, tiers) {
    return fetchAPI(`/api/circles/${circleId}/plan/approve`, {
      method: 'POST',
      body: JSON.stringify({ missionText, tiers })
    });
  },

  async completeTodo(circleId, todoId) {
    return fetchAPI(`/api/circles/${circleId}/todos/${todoId}/complete`, { method: 'POST' });
  },

  async getMessages(circleId, sinceId = 0) {
    return fetchAPI(`/api/circles/${circleId}/messages?sinceId=${sinceId}`);
  },

  async markRead(circleId, lastMessageId = 0) {
    return fetchAPI(`/api/circles/${circleId}/read`, {
      method: 'POST',
      body: JSON.stringify({ lastMessageId })
    });
  },

  async sendMessage(circleId, message) {
    return fetchAPI(`/api/circles/${circleId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  },

  // Personal-todo sharing (opt-in)
  async getShareableTodos(circleId) {
    return fetchAPI(`/api/circles/${circleId}/shareable-todos`);
  },

  async shareTodo(circleId, todoId) {
    return fetchAPI(`/api/circles/${circleId}/share-todo`, {
      method: 'POST',
      body: JSON.stringify({ todoId })
    });
  },

  async unshareTodo(circleId, todoId) {
    return fetchAPI(`/api/circles/${circleId}/unshare-todo`, {
      method: 'POST',
      body: JSON.stringify({ todoId })
    });
  },

  // Realtime chat stream (SSE over fetch — EventSource can't send the JWT).
  // Returns the raw Response; the caller reads response.body.
  streamUrl(circleId) {
    return `${process.env.REACT_APP_API_URL}/api/circles/${circleId}/stream`;
  }
};

/**
 * Feature flags API — which optional features this deployment has switched on
 * (server env vars, so a flag flip needs no client rebuild). Public/pre-auth.
 */
export const featuresAPI = {
  async getFeatures() {
    return fetchAPI('/api/features');
  }
};

/**
 * Family & Friends API — a family account covers up to N people's Sophia access.
 */
export const familyAPI = {
  async getFamily() {
    return fetchAPI('/api/family/me');
  },

  async invite(email) {
    return fetchAPI('/api/family/invite', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  async remove(seatId) {
    return fetchAPI('/api/family/remove', {
      method: 'POST',
      body: JSON.stringify({ seatId })
    });
  }
};

/**
 * Billing API — B2C plans. Runs against a mock provider until Stripe keys exist
 * (`mode` tells you which); the client flow is identical either way.
 */
export const billingAPI = {
  async getBilling() {
    return fetchAPI('/api/billing/me');
  },

  /** Pull-based activation after the Stripe Checkout return (no webhook needed). */
  async syncCheckoutSession(sessionId) {
    return fetchAPI('/api/billing/sync-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId })
    });
  },

  /** Composed bundle preview for this buyer — no Stripe session created. */
  async previewCheckout(plan) {
    return fetchAPI(`/api/billing/preview?plan=${encodeURIComponent(plan)}`);
  },

  async checkout(plan) {
    return fetchAPI('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan })
    });
  },

  // Mock-mode only: stands in for the Stripe webhook.
  async confirm(plan) {
    return fetchAPI('/api/billing/confirm', {
      method: 'POST',
      body: JSON.stringify({ plan })
    });
  },

  /**
   * Cancel entitlements. scope: 'together' (just the add-on — seats drop,
   * own base access survives) | 'base' | 'all' (default).
   */
  async cancel(scope = 'all') {
    return fetchAPI('/api/billing/cancel', {
      method: 'POST',
      body: JSON.stringify({ scope })
    });
  }
};

/**
 * Coaching credits API — purchased coaching-session credits (Stripe one-time
 * checkout: single session or the pack). mode 'off' = Stripe prices not
 * configured yet; booking is then ungated and the purchase UI hidden.
 */
export const coachingCreditsAPI = {
  /** { mode, balance, purchased, used, packages: [{id, name, credits, amount, priceLabel}] } */
  async getCredits() {
    return fetchAPI('/api/coaching-credits');
  },

  /** Start Stripe Checkout for 'single' | 'pack' → { url } */
  async checkout(packageId) {
    return fetchAPI('/api/coaching-credits/checkout', {
      method: 'POST',
      body: JSON.stringify({ package: packageId })
    });
  },

  /** Pull-based recording after the Stripe Checkout return (no webhook needed). */
  async syncCheckoutSession(sessionId) {
    return fetchAPI('/api/coaching-credits/sync-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId })
    });
  }
};

/**
 * Community Manager Hub API — the private resource page for community
 * leaders. Gated server-side (Community-org admin or Security/Dev-Panel
 * access); media-kit downloads go straight through fetch in CommunityHub.jsx
 * because they need a blob, not JSON.
 */
export const communityHubAPI = {
  async getHub() {
    return fetchAPI('/api/community-hub');
  }
};

// Events — upcoming member sessions (Zoom / Luma links), JWT-gated.
export const eventsAPI = {
  /** @returns {Promise<{ upcoming: object[], past: object[] }>} */
  async list() {
    return fetchAPI('/api/events');
  }
};

export { getToken, setToken, removeToken };
